import React from 'react';
import { ACTIVE_GREEN, ACTIVE_GREEN_BG, getStatusChipSx, INACTIVE_STATUS_BG, INACTIVE_STATUS_COLOR } from '../../../constants/statusColors';
import {
  Paper,
  Typography,
  Grid,
  Box,
  Card,
  CardContent,
  Chip,
} from '@mui/material';
import {
  BusinessOutlined as CompanyIcon,
  BusinessOutlined,
  DescriptionOutlined as LicenseIcon,
  DescriptionOutlined,
  CalendarTodayOutlined as DateIcon,
  CategoryOutlined as TypeIcon,
  HomeOutlined as AddressIcon,
  LocalPostOfficeOutlined as POBoxIcon,
  LocationCityOutlined as CityIcon,
  PublicOutlined as CountryIcon,
  PhoneOutlined as PhoneIcon,
  EmailOutlined as EmailIcon,
  LanguageOutlined as WebsiteIcon,
  PersonOutlined as CSIcon,
  PersonOutlined,
  AccountTreeOutlined as ParentIcon,
  AccountTreeOutlined,
  ShowChartOutlined as ShareholdingIcon,
  AttachMoneyOutlined as CapitalIcon,
  AccountBalanceOutlined as EntityClassIcon,
  GroupsOutlined as EmployeesIcon,
  PersonOutlined as CeoIcon,
  CalendarTodayOutlined as FoundedIcon,
} from '@mui/icons-material';
import { formatDisplayDate } from '../../../utils/apiMappers';
import { formatShareholdingPercent } from '../../../utils/shareholdingPercent';
import { useLookups } from '../../../hooks/useLookups';

const OverviewTab = ({ company, counts = {} }) => {
  // Stats sourced from the parent's real counts (fall back to 0)
  const totalDirectors = counts.directors ?? 0;
  const totalCommittees = counts.committees ?? 0;
  const totalDocuments = counts.documents ?? 0;
  const subsidiaries = counts.subsidiaries ?? 0;

  const { items: entityClassifications } = useLookups('ENTITY_CLASSIFICATION');
  
  // Get entity classification label
  const entityClassificationLabel = entityClassifications.find(
    (item) => item.code === company.entity_classification || item.code === company.entityClassification
  )?.label || company.entity_classification || company.entityClassification;

  const StatCard = ({ icon: Icon, label, value, color }) => (
    <Card sx={{ height: '100%', borderRadius: '12px' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '8px',
              bgcolor: `${color}15`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: color,
            }}
          >
            <Icon sx={{ fontSize: 24 }} />
          </Box>
        </Box>
        <Typography variant="h4" fontWeight={600} color={color}>
          {value}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          {label}
        </Typography>
      </CardContent>
    </Card>
  );

  const InfoItem = ({ icon: Icon, label, value, iconBg, iconColor }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2.5 }}>
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: '12px',
          bgcolor: iconBg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Icon sx={{ fontSize: 24, color: iconColor }} />
      </Box>
      <Box sx={{ flex: 1 }}>
        <Typography variant="caption" color="textSecondary" display="block">
          {label}
        </Typography>
        <Typography variant="body1" fontWeight={500}>
          {value || '-'}
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box>
      {/* Stat Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={PersonOutlined}
            label="Directors"
            value={totalDirectors}
            color="#1976D2"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={BusinessOutlined}
            label="Committees"
            value={totalCommittees}
            color={ACTIVE_GREEN}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={DescriptionOutlined}
            label="Documents"
            value={totalDocuments}
            color="#E65100"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            icon={AccountTreeOutlined}
            label="Subsidiaries"
            value={subsidiaries}
            color="#7B1FA2"
          />
        </Grid>
      </Grid>

      {/* Main Information Sections */}
      <Grid container spacing={3}>
        {/* Company Information */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%', borderRadius: '12px' }}>
            <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
              Company Information
            </Typography>

            <InfoItem
              icon={CompanyIcon}
              label="Company Name"
              value={company.name}
              iconBg="#E3F2FD"
              iconColor="#1976D2"
            />

            <InfoItem
              icon={LicenseIcon}
              label="Trade License Number"
              value={company.tradeLicenseNumber}
              iconBg="#FFF3E0"
              iconColor="#E65100"
            />

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2.5 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '12px',
                  bgcolor: company.structureType === 'Parent' ? '#E3F2FD' : '#F3E5F5',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <TypeIcon sx={{ fontSize: 24, color: company.structureType === 'Parent' ? '#1976D2' : '#7B1FA2' }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" color="textSecondary" display="block">
                  Company Classification
                </Typography>
                <Chip
                  label={company.governanceTypeName || company.company_type_name || company.type}
                  sx={{
                    bgcolor: '#F3E5F5',
                    color: '#7B1FA2',
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    height: 24,
                    borderRadius: '12px',
                    border: '1px solid #7B1FA2',
                    mt: 0.5,
                  }}
                  size="small"
                  variant="outlined"
                />
                <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 0.5 }}>
                  Structure: {company.structureType || '—'}
                </Typography>
              </Box>
            </Box>

            {company.structureType === 'Subsidiary' && (
              <>
                <InfoItem
                  icon={ParentIcon}
                  label="Parent Company"
                  value={company.parentCompany || company.parent_company_name || '—'}
                  iconBg={ACTIVE_GREEN_BG}
                  iconColor={ACTIVE_GREEN}
                />

                <InfoItem
                  icon={ShareholdingIcon}
                  label="Shareholding"
                  value={formatShareholdingPercent(company.shareholdingPercentage)}
                  iconBg="#FFF3E0"
                  iconColor="#E65100"
                />
              </>
            )}

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2.5 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '12px',
                  bgcolor: company.status === 'ACTIVE' ? ACTIVE_GREEN_BG : INACTIVE_STATUS_BG,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <CompanyIcon sx={{ fontSize: 24, color: company.status === 'ACTIVE' ? ACTIVE_GREEN : INACTIVE_STATUS_COLOR }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" color="textSecondary" display="block">
                  Status
                </Typography>
                <Chip
                  label={company.status}
                  sx={{
                    ...getStatusChipSx(company.status),
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    height: 24,
                    borderRadius: '12px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    mt: 0.5,
                  }}
                  size="small"
                />
              </Box>
            </Box>

            <InfoItem
              icon={DateIcon}
              label="Created Date"
              value={formatDisplayDate(company.createdDate)}
              iconBg="#F5F5F5"
              iconColor="#757575"
            />
          </Paper>
        </Grid>

        {/* Contact Information */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%', borderRadius: '12px' }}>
            <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
              Contact Information
            </Typography>

            <InfoItem
              icon={AddressIcon}
              label="Address"
              value={company.address}
              iconBg="#E3F2FD"
              iconColor="#1976D2"
            />

            <InfoItem
              icon={POBoxIcon}
              label="P.O. Box"
              value={company.poBox}
              iconBg="#F3E5F5"
              iconColor="#7B1FA2"
            />

            <InfoItem
              icon={CityIcon}
              label="City"
              value={company.city}
              iconBg={ACTIVE_GREEN_BG}
              iconColor={ACTIVE_GREEN}
            />

            <InfoItem
              icon={CountryIcon}
              label="Country"
              value={company.country}
              iconBg="#FFF3E0"
              iconColor="#E65100"
            />

            <InfoItem
              icon={PhoneIcon}
              label="Phone"
              value={company.phone}
              iconBg="#E3F2FD"
              iconColor="#1976D2"
            />

            <InfoItem
              icon={EmailIcon}
              label="Email"
              value={company.email}
              iconBg="#FFEBEE"
              iconColor="#C62828"
            />

            <InfoItem
              icon={WebsiteIcon}
              label="Website"
              value={company.website}
              iconBg="#F5F5F5"
              iconColor="#757575"
            />
          </Paper>
        </Grid>

        {/* Corporate Profile */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3, borderRadius: '12px' }}>
            <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
              Corporate Profile
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6} lg={3}>
                <InfoItem
                  icon={CapitalIcon}
                  label="Capital"
                  value={company.capital || company.revenue}
                  iconBg={ACTIVE_GREEN_BG}
                  iconColor={ACTIVE_GREEN}
                />
              </Grid>
              {(company.entity_classification || company.entityClassification) && (
                <Grid item xs={12} md={6} lg={3}>
                  <InfoItem
                    icon={EntityClassIcon}
                    label="Entity Classification"
                    value={entityClassificationLabel}
                    iconBg="#E8F5E9"
                    iconColor="#2E7D32"
                  />
                </Grid>
              )}
              <Grid item xs={12} md={6} lg={3}>
                <InfoItem
                  icon={FoundedIcon}
                  label="Year Founded"
                  value={company.foundedYear || company.founded_year}
                  iconBg="#FFF3E0"
                  iconColor="#E65100"
                />
              </Grid>
              <Grid item xs={12} md={6} lg={3}>
                <InfoItem
                  icon={EmployeesIcon}
                  label="Employees"
                  value={company.employeeCount ?? company.employee_count}
                  iconBg="#E3F2FD"
                  iconColor="#1976D2"
                />
              </Grid>
              <Grid item xs={12} md={6} lg={3}>
                <InfoItem
                  icon={CeoIcon}
                  label="CEO"
                  value={company.ceoName || company.ceo_name}
                  iconBg="#F3E5F5"
                  iconColor="#7B1FA2"
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 1 }}>
                  Profile Summary
                </Typography>
                <Typography variant="body1">
                  {company.profileSummary || company.profile_summary || '—'}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Company Secretary */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3, borderRadius: '12px' }}>
            <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
              Assigned Company Secretary
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <InfoItem
                  icon={CSIcon}
                  label="Company Secretary"
                  value={company.assignedCS}
                  iconBg="#E3F2FD"
                  iconColor="#1976D2"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <InfoItem
                  icon={EmailIcon}
                  label="CS Email"
                  value={company.assignedCSEmail}
                  iconBg="#FFEBEE"
                  iconColor="#C62828"
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default OverviewTab;
